'use client'

import { type ReactNode, createContext, useContext, useState } from 'react'

import type { TopNavV6Props, TopNavV6SubNavItem } from './TopNavV6Props'

interface TopNavV6ContextType {
  expandedNavItem: string | null
  setExpandedNavItem: (item: string | null) => void
  isSubNavExpanded: boolean
  setSubNavExpanded: (expanded: boolean) => void
  isMobileMenuOpen: boolean
  setIsMobileMenuOpen: (open: boolean) => void
  selectedThirdLevelMenu: TopNavV6SubNavItem | null
  setSelectedThirdLevelMenu: (item: TopNavV6SubNavItem | null) => void
  isAccountExpanded: boolean
  setAccountExpanded: (expanded: boolean) => void
  account?: TopNavV6Props['account']
}

const TopNavV6Context = createContext<TopNavV6ContextType>({
  expandedNavItem: null,
  setExpandedNavItem: () => {},
  isSubNavExpanded: false,
  setSubNavExpanded: () => {},
  isMobileMenuOpen: false,
  setIsMobileMenuOpen: () => {},
  selectedThirdLevelMenu: null,
  setSelectedThirdLevelMenu: () => {},
  isAccountExpanded: false,
  setAccountExpanded: () => {},
  account: undefined,
})

export const TopNavV6Provider = ({
  children,
  account,
}: {
  children: ReactNode
  account?: TopNavV6Props['account']
}) => {
  const [expandedNavItem, setExpandedNavItem] = useState<string | null>(null)
  const [isSubNavExpanded, setSubNavExpanded] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [selectedThirdLevelMenu, setSelectedThirdLevelMenu] = useState<TopNavV6SubNavItem | null>(
    null
  )
  const [isAccountExpanded, setAccountExpanded] = useState(false)

  return (
    <TopNavV6Context.Provider
      value={{
        expandedNavItem,
        setExpandedNavItem,
        isSubNavExpanded,
        setSubNavExpanded,
        isMobileMenuOpen,
        setIsMobileMenuOpen,
        selectedThirdLevelMenu,
        setSelectedThirdLevelMenu,
        isAccountExpanded,
        setAccountExpanded,
        account,
      }}
    >
      {children}
    </TopNavV6Context.Provider>
  )
}

export const useTopNavV6 = () => {
  const context = useContext(TopNavV6Context)
  if (!context) {
    throw new Error('useTopNavV6 must be used within a TopNavV6Provider')
  }
  return context
}
